/**
 * Finished strokes are baked into an offscreen layer so a long drawing session
 * stays at 60fps — each frame blits one image instead of re-stroking hundreds
 * of paths.
 */

import type { Stroke } from './stroke.ts';
import { drawStroke } from './render.ts';

export class StrokeLayer {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private width = 0;
  private height = 0;
  private dpr = 1;

  resize(width: number, height: number, dpr: number): void {
    if (typeof document === 'undefined') return;
    if (this.width === width && this.height === height && this.dpr === dpr) return;
    this.width = width;
    this.height = height;
    this.dpr = dpr;
    this.canvas = this.canvas ?? document.createElement('canvas');
    this.canvas.width = Math.max(1, Math.round(width * dpr));
    this.canvas.height = Math.max(1, Math.round(height * dpr));
    this.ctx = this.canvas.getContext('2d');
    this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  bake(stroke: Stroke): void {
    if (this.ctx) drawStroke(this.ctx, stroke);
  }

  /** Draw arbitrary content into the baked layer (mirrored copies, stamps). */
  paint(fn: (ctx: CanvasRenderingContext2D) => void): void {
    if (this.ctx) fn(this.ctx);
  }

  /** Redraw from scratch — used by undo and by resize. */
  rebuild(strokes: Stroke[]): void {
    if (!this.ctx) return;
    this.ctx.clearRect(0, 0, this.width, this.height);
    for (const s of strokes) drawStroke(this.ctx, s);
  }

  clear(): void {
    this.ctx?.clearRect(0, 0, this.width, this.height);
  }

  blit(target: CanvasRenderingContext2D): void {
    if (this.canvas && this.width > 0) target.drawImage(this.canvas, 0, 0, this.width, this.height);
  }
}
